import { FC, useState } from 'react';
import { TextField, Tooltip, IconButton } from '@mui/material';
import { FolderOpen as FolderOpenIcon } from '@mui/icons-material';
import CompanyDocsForm from '../dialogs/CompanyDocsForm';
import { useDocs } from '../items/hooks/HDocs';
import { CompanyRecord } from '../items/hooks/HCompanies';
import { EditButton, DeleteButton } from '../utils/buttons';
import ConfirmForm from '../dialogs/ConfirmForm';

type GCompaniesTableProps = {
    companies: CompanyRecord[];
    onEditCompany?: (company: CompanyRecord) => void;
    editRow?: number | null;
    editValues?: { [key: string]: string };
    setEditRow?: (row: number | null) => void;
    setEditValues?: (values: { [key: string]: string }) => void;
    handleEditSave?: (company: CompanyRecord) => void;
    handleDelete?: (company: CompanyRecord) => void;
};

const getCompanyId = (company: CompanyRecord) => company.id ?? company.company_id ?? '-';

const getCompanyName = (company: CompanyRecord) =>
    company.company_name ?? company.company ?? company.name ?? '-';

const getCompanyAddress = (company: CompanyRecord) => company.address ?? '-';

const getCompanyCountry = (company: CompanyRecord) => company.country ?? '-';

const GCompaniesTable: FC<GCompaniesTableProps> = ({
    companies,
    onEditCompany,
    editRow,
    editValues = {},
    setEditRow,
    setEditValues,
    handleEditSave,
    handleDelete,
}) => {
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; company: CompanyRecord | null }>({
        open: false,
        company: null,
    });
    const [docsDialogOpen, setDocsDialogOpen] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string | number | null>(null);
    const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(null);
    const { createObjectMedia } = useDocs();

    return (
        <>
            <table className='elegant-table users-rounded-table'>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Company</th>
                        <th>Address</th>
                        <th>Country</th>
                        <th>Documents</th>
                        <th style={{ width: '220px' }}>Edit</th>
                    </tr>
                </thead>
                <tbody>
                    {companies.map((company, index) => (
                        <tr key={String(getCompanyId(company)) || index}>
                            {editRow === index && setEditValues && setEditRow && handleEditSave ? (
                                <>
                                    <td>{getCompanyId(company)}</td>
                                    <td>{getCompanyName(company)}</td>
                                    <td>
                                        <TextField
                                            value={editValues.address || ''}
                                            onChange={(event) =>
                                                setEditValues({ ...editValues, address: event.target.value })
                                            }
                                            size='small'
                                            fullWidth
                                        />
                                    </td>
                                    <td>
                                        <TextField
                                            value={editValues.country || ''}
                                            onChange={(event) =>
                                                setEditValues({ ...editValues, country: event.target.value })
                                            }
                                            size='small'
                                            fullWidth
                                        />
                                    </td>
                                    <td>
                                        <button
                                            className='icon-blue'
                                            onClick={() =>
                                                handleEditSave({
                                                    ...company,
                                                    address: editValues.address ?? getCompanyAddress(company),
                                                    country: editValues.country ?? getCompanyCountry(company),
                                                })
                                            }
                                        >
                                            Save
                                        </button>
                                        <button className='icon-blue' onClick={() => setEditRow(null)}>
                                            Cancel
                                        </button>
                                        {handleDelete && (
                                            <button
                                                className='icon-red'
                                                onClick={() => setDeleteConfirm({ open: true, company })}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td>{getCompanyId(company)}</td>
                                    <td>{getCompanyName(company)}</td>
                                    <td>{getCompanyAddress(company)}</td>
                                    <td>{getCompanyCountry(company)}</td>
                                    <td>
                                        <Tooltip title='Documents'>
                                            <IconButton
                                                size='small'
                                                color='primary'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedCompanyId(getCompanyId(company));
                                                    setSelectedCompanyName(getCompanyName(company));
                                                    setDocsDialogOpen(true);
                                                }}
                                            >
                                                <FolderOpenIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                    <td style={{ width: '220px' }}>
                                        {onEditCompany || (setEditRow && setEditValues && handleEditSave) ? (
                                            <EditButton
                                                onClick={() => {
                                                    if (onEditCompany) {
                                                        onEditCompany(company);
                                                        return;
                                                    }

                                                    setEditRow(index);
                                                    setEditValues({
                                                        address: company.address ?? '',
                                                        country: company.country ?? '',
                                                    });
                                                }}
                                            />
                                        ) : (
                                            '-'
                                        )}
                                        {handleDelete && <DeleteButton onClick={() => setDeleteConfirm({ open: true, company })} />}
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            <ConfirmForm
                open={deleteConfirm.open}
                title='Confirm Deletion'
                content={`Are you sure you want to delete company ${getCompanyName(deleteConfirm.company || {})}?`}
                onCancel={() => setDeleteConfirm({ open: false, company: null })}
                onConfirm={() => {
                    if (deleteConfirm.company && handleDelete) {
                        handleDelete(deleteConfirm.company);
                        setEditRow?.(null);
                    }
                    setDeleteConfirm({ open: false, company: null });
                }}
            />
            <CompanyDocsForm
                open={docsDialogOpen}
                onClose={() => {
                    setDocsDialogOpen(false);
                    setSelectedCompanyId(null);
                    setSelectedCompanyName(null);
                }}
                companyId={selectedCompanyId}
                companyName={selectedCompanyName}
                canUpload={true}
                onFileDrop={async (file, companyId) => {
                    const ok = await createObjectMedia(companyId, file, 'companies');
                    if (!ok) throw new Error('Failed to upload company document');
                }}
            />
        </>
    );
};

export default GCompaniesTable;